import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  // useCallback, // Not used in current implementation
  ReactNode,
} from "react";
import { useAPI } from "./APIContext";
import { useNavigate, useParams } from "react-router-dom";

// Types
export interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  attachment_ids?: string[];
  image_url?: string;
  document_name?: string;
  document_names?: string[];
  sources?: { [key: string]: { title: string; url: string; snippet?: string } };
}

export interface Conversation {
  id: string;
  title: string;
  created_at: string;
  model: string;
  messages: Message[];
  project_id?: string;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  instructions?: string;
  created_at: string;
  files: ProjectDocument[];
  conversations: Conversation[];
}

export interface ProjectDocument {
  id: string;
  name: string;
  size: number;
  type: string;
  created_at: string;
}

// type APIMessage = {
//   _id: string;
//   assistant_role?: string;
//   chat_id: string;
//   content_type: string;
//   created_at: string;
//   order: number;
//   user_id: string;
//   user_role?: string;
// };

interface ConversationContextType {
  currentConversation: Conversation | null;
  conversations: Conversation[];
  loading: boolean;
  loadingMessage: boolean;
  selectedModel: string;
  attachments: { id: string; filename: string }[];
  activeConversationId: string | null;
  chatHistory: any[];
  createProject: boolean;
  projectChat: boolean;
  currentProject: Project | null;
  projects: Project[];
  selectedMode: "internal" | "external";
  setSelectedMode: (mode: "internal" | "external") => void;
  loadProjectConversations: (projectId: string) => Promise<void>;
  setCreateProject: (value: boolean) => void;
  setProjectChat: (value: boolean) => void;
  setSelectedModel: (model: string) => void;
  sendMessage: (
    message: string,
    response?: string,
    user_id?: string,
    image_url?: string,
    document_names?: string[],
    sources?: {
      [key: string]: { title: string; url: string; snippet?: string };
    }
  ) => Promise<void>;
  loadConversation: (conversationId: string) => Promise<void>;
  startNewConversation: (userId?: string) => void;
  deleteConversation: (
    conversationId: string,
    userId?: string
  ) => Promise<void>;
  addAttachment: (file: File) => Promise<void>;
  removeAttachment: (attachmentId: string) => void;
  setLoadingMessage: (loading: boolean) => void;
  setCurrentConversation: React.Dispatch<
    React.SetStateAction<Conversation | null>
  >;
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
  setActiveConversationId: (id: string | null) => void;
  setChatHistory: React.Dispatch<React.SetStateAction<any[]>>;
  fetchChatHistory: (
    user_id: string,
    page?: number,
    limit?: number
  ) => Promise<void>;
  fetchAndSetConversation: (chatId: string, user_id: string) => Promise<void>;
  createNewProject: (
    name: string,
    description: string,
    instructions?: string
  ) => Promise<void>;
  addProjectFiles: (files: File[], documentNames: string[]) => Promise<void>;
  removeProjectFile: (fileName: string) => void;
  setCurrentProject: (project: Project | null) => void;
  fetchProjects: () => Promise<void>;
  startNewProjectConversation: (projectId: string) => void;
  streamingText: string;
  isStreaming: boolean;
  setStreamingText: (text: string) => void;
  setIsStreaming: (streaming: boolean) => void;
}

// Create context
const ConversationContext = createContext<ConversationContextType | undefined>(
  undefined
);

// Provider component
export const ConversationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const api = useAPI();
  const navigate = useNavigate();
  const { conversationId } = useParams();

  const [currentConversation, setCurrentConversation] =
    useState<Conversation | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<boolean>(false);
  const [streamingText, setStreamingText] = useState<string>("");
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [selectedModel, setSelectedModel] = useState<string>("gpt-4o");
  const [attachments, setAttachments] = useState<
    { id: string; filename: string }[]
  >([]);
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  const [chatHistory, setChatHistory] = useState<any[]>([]);
  const [createProject, setCreateProject] = useState<boolean>(false);
  const [projectChat, setProjectChat] = useState<boolean>(false);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedMode, setSelectedMode] = useState<"internal" | "external">(
    "external"
  );
  const [fetchHistoryTimeout, setFetchHistoryTimeout] =
    useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const loadConversations = async () => {
      try {
        setLoading(true);
        const data = await api.getConversations();
        setConversations(data);
        setLoading(false);
      } catch (error) {
        console.error("Error loading conversations:", error);
        setLoading(false);
      }
    };

    loadConversations();
  }, [api]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (fetchHistoryTimeout) {
        clearTimeout(fetchHistoryTimeout);
      }
    };
  }, [fetchHistoryTimeout]);

  const loadConversation = async (id: string) => {
    try {
      setLoading(true);
      const data = await api.getConversation(id);
      setCurrentConversation(data);
      setLoading(false);
    } catch (error) {
      console.error(`Error loading conversation ${id}:`, error);
      setLoading(false);
      navigate("/");
    }
  };

  useEffect(() => {
    if (conversationId) {
      loadConversation(conversationId);
    } else {
      setCurrentConversation(null);
      setAttachments([]);
    }
  }, [conversationId]);

  const startNewConversation = async (userId?: string) => {
    try {
      // Clear current conversation state - let backend handle session creation
      setCurrentConversation(null);
      setAttachments([]);
      setActiveConversationId(null);
      sessionStorage.removeItem("conversation_id");
      sessionStorage.removeItem("session_id");
      sessionStorage.removeItem("thread_id");
      navigate("/");
    } catch (error) {
      console.error("Error starting new conversation:", error);
      // Fallback to existing logic
      setCurrentConversation(null);
      setAttachments([]);
      setActiveConversationId(null);
      sessionStorage.removeItem("conversation_id");
      navigate("/");
    }
  };

  const sendMessage: ConversationContextType["sendMessage"] = async (
    message,
    response,
    user_id,
    image_url,
    document_names,
    sources
  ) => {
    try {
      const attachmentIds = attachments.map((attachment) => attachment.id);
      const timestamp = new Date().toISOString();

      let updatedMessages = currentConversation
        ? [...currentConversation.messages]
        : [];
      
      // 🎯 CHECK FOR DUPLICATE USER MESSAGE (avoid adding if already exists)
      const lastMessage = updatedMessages[updatedMessages.length - 1];
      const isDuplicateUserMessage = lastMessage && 
        lastMessage.role === "user" && 
        lastMessage.content === message &&
        Math.abs(new Date(lastMessage.timestamp).getTime() - new Date(timestamp).getTime()) < 10000; // Within 10 seconds
      
      if (!isDuplicateUserMessage) {
        const userMessage: Message = {
          role: "user",
          content: message,
          timestamp,
          attachment_ids: attachmentIds,
          ...(document_names ? { document_names } : {}),
        };
        updatedMessages.push(userMessage);
      }

      if (!currentConversation) {
        const newConversation: Conversation = {
          id: "",
          title: (window as any).sanitizeTitle ? (window as any).sanitizeTitle(message, 50, "New Chat") : (message.slice(0, 50) + (message.length > 50 ? "..." : "")),
          created_at: new Date().toISOString(),
          model: selectedModel,
          messages: updatedMessages,
        };
        setCurrentConversation(newConversation);
      } else {
        setCurrentConversation((prev) =>
          prev ? { ...prev, messages: updatedMessages } : null
        );
      }

      if (response !== undefined || image_url !== undefined) {
        setCurrentConversation((prev) => {
          if (!prev) return null;

          const assistantMessage: Message = {
            role: "assistant",
            content: response || "",
            timestamp: new Date().toISOString(),
            ...(image_url ? { image_url } : {}),
            ...(sources ? { sources } : {}),
          };

          return {
            ...prev,
            messages: [...prev.messages, assistantMessage],
          };
        });
      }

      setAttachments([]);
    } catch (error) {
      console.error("Error sending message:", error);
      throw error;
    }
  };

  const deleteConversation = async (id: string, userId?: string) => {
    try {
      if (selectedMode === "internal") {
        // For internal mode, use v2 API
        const response = await api.v2DeleteChatSession(id);
        if (response.success) {
          setChatHistory((prev) => prev.filter((chat) => chat.id !== id));
          if (currentConversation && currentConversation.id === id) {
            // Clear current conversation without creating a new one
            setCurrentConversation(null);
            setActiveConversationId(null);
            sessionStorage.removeItem("conversation_id");
            navigate("/");
          }
        }
      } else {
        // For external mode, use existing API
        const response = await api.deleteConversation(id);
        if (response.success) {
          setChatHistory((prev) => prev.filter((chat) => chat._id !== id));
          if (currentConversation && currentConversation.id === id) {
            // Clear current conversation without creating a new one
            setCurrentConversation(null);
            setActiveConversationId(null);
            sessionStorage.removeItem("conversation_id");
            navigate("/");
          }
        } else {
          console.error("Failed to delete conversation:", response.error);
        }
      }
    } catch (error) {
      console.error(`Error deleting conversation ${id}:`, error);
    }
  };

  const addAttachment = async (file: File) => {
    try {
      const response = await api.uploadAttachment(file);
      setAttachments((prev) => [
        ...prev,
        { id: response.id, filename: response.filename },
      ]);
    } catch (error) {
      console.error("Error uploading attachment:", error);
    }
  };

  const removeAttachment = (attachmentId: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
  };

  const fetchChatHistory = async (user_id: string, page = 1, limit = 10) => {
    // Clear any existing timeout
    if (fetchHistoryTimeout) {
      clearTimeout(fetchHistoryTimeout);
    }

    // Set a new timeout to debounce the call
    const timeout = setTimeout(async () => {
      try {
        const data = await api.getChatHistory(
          user_id,
          page,
          limit,
          selectedMode
        );
        if (selectedMode === "internal") {
          // For internal mode, handle the sessions array from v2 API
          const sessions = data.sessions || [];

          // Map sessions to the expected format - backend returns _id, not id
          const sessionsWithTitles = sessions.map((session: any) => ({
            id: session._id || session.id, // Handle both _id and id
            title: session.title || "Chat Session",
            created_at: session.created_at || new Date().toISOString(),
            session_id: session._id || session.id,
          }));

          setChatHistory(sessionsWithTitles);

          // Auto-set the most recent session as active if it matches the current session ID
          if (sessionsWithTitles.length > 0) {
            const mostRecentSession = sessionsWithTitles[0]; // Assuming sessions are sorted by most recent first
            const currentSessionId = sessionStorage.getItem("conversation_id");

            // If the most recent session matches the current session ID, set it as active
            if (currentSessionId && mostRecentSession.id === currentSessionId) {
              setActiveConversationId(mostRecentSession.id);
            }
          }
        } else {
          setChatHistory(data.chats || []);

          // Auto-set the most recent chat as active if it matches the current conversation ID
          if (data.chats && data.chats.length > 0) {
            const mostRecentChat = data.chats[0]; // Assuming chats are sorted by most recent first
            const currentConversationId =
              sessionStorage.getItem("conversation_id");

            // If the most recent chat matches the current conversation ID, set it as active
            if (
              currentConversationId &&
              mostRecentChat._id === currentConversationId
            ) {
              setActiveConversationId(mostRecentChat._id);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching chat history:", error);
      }
    }, 500); // 500ms debounce

    setFetchHistoryTimeout(timeout);
  };

  const fetchAndSetConversation = async (chatId: string, user_id: string) => {
    try {
      if (selectedMode === "internal") {
        // Switch to the session
        try {
          await api.v2SwitchChatSession(chatId);
        } catch (switchError) {
          console.error("Error switching to session:", switchError);
        }

        // Fetch the chat session
        const data = await api.v2GetChatSession(chatId);
        if (data.success && data.messages) {
          let title = data.session?.title || "Chat Session";

          setCurrentConversation({
            id: data.session?._id || chatId,
            title: title,
            created_at: data.session?.created_at || new Date().toISOString(),
            model: selectedModel,
            messages: data.messages.map((m: any) => ({
              role: m.role,
              content: m.content,
              timestamp: m.created_at || new Date().toISOString(),
            })),
          });

          sessionStorage.setItem(
            "conversation_id",
            data.session?._id || chatId
          );
          setActiveConversationId(data.session?._id || chatId);
        }
      } else {
        // External mode
        const data = await api.getChatById(chatId, user_id, selectedMode);

        setCurrentConversation({
          id: data.chat._id,
          title: data.chat.title,
          created_at: data.chat.created_at,
          model: data.chat.model_name,
          messages: data.messages.flatMap((m: any) => {
            const messages = [];

            // Create user message if user_role exists
            if (m.user_role) {
              messages.push({
                role: "user" as const,
                content: m.user_role,
                timestamp: m.created_at || new Date().toISOString(),
              });
            }

            // Create assistant message if assistant_role exists
            if (m.assistant_role) {
              messages.push({
                role: "assistant" as const,
                content: m.assistant_role,
                timestamp: m.created_at || new Date().toISOString(),
              });
            }

            // Fallback to old format if neither exists
            if (!m.user_role && !m.assistant_role) {
              messages.push({
                role: (m.role || "user") as "user" | "assistant",
                content: m.content || "",
                timestamp: m.created_at || new Date().toISOString(),
              });
            }

            return messages;
          }),
        });
      }
    } catch (error) {
      console.error("Error loading chat:", error);
    }
  };

  const createNewProject = async (
    name: string,
    description: string,
    instructions?: string
  ) => {
    const user_id = sessionStorage.getItem("user_id") || "1";
    const response = await api.createProject(
      user_id,
      name,
      description,
      instructions
    );
    if (response.success && response.project) {
      const newProject: Project = {
        id: response.project.id,
        name: response.project.name,
        description: response.project.goal,
        instructions,
        created_at: response.project.created_at,
        files: [],
        conversations: [],
      };
      setProjects((prev) => [...prev, newProject]);
      setCurrentProject(newProject);
      setCreateProject(false);
      setProjectChat(false);
    }
  };

  const addProjectFiles = async (files: File[], documentNames: string[]) => {
    if (currentProject) {
      const user_id = sessionStorage.getItem("user_id") || "1";
      const uploadedDocs = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const name = documentNames[i] || file.name;
        const response = await api.uploadProjectDocument(
          currentProject.id,
          user_id,
          name,
          file
        );
        if (response.success && response.document) {
          uploadedDocs.push({
            id: response.document.id,
            name: response.document.name,
            size: response.document.size_mb,
            type: response.document.type,
            created_at: response.document.created_at,
          });
        }
      }
      const updatedProject = {
        ...currentProject,
        files: [...currentProject.files, ...uploadedDocs],
      };
      setCurrentProject(updatedProject);
      setProjects((prev) =>
        prev.map((p) => (p.id === currentProject.id ? updatedProject : p))
      );
    }
  };

  const removeProjectFile = (fileName: string) => {
    if (currentProject) {
      const updatedProject = {
        ...currentProject,
        files: currentProject.files.filter((f) => f.name !== fileName),
      };
      setCurrentProject(updatedProject);
      setProjects((prev) =>
        prev.map((p) => (p.id === currentProject.id ? updatedProject : p))
      );
    }
  };

  const fetchProjects = async () => {
    try {
      const user_id = sessionStorage.getItem("user_id") || "1";
      const response = await api.getProjects(user_id);
      if (response.projects) {
        const mappedProjects = response.projects.map((p: any) => ({
          id: p._id,
          name: p.name,
          description: p.goal,
          created_at: p.created_at,
          instructions: "",
          files: [],
          conversations: [],
        }));
        setProjects(mappedProjects);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const loadProjectConversations = async (projectId: string) => {
    try {
      setLoading(true);
      const user_id = sessionStorage.getItem("user_id") || "1";
      const projectDetails = await api.getProjectDetails(projectId, user_id);

      if (projectDetails.conversations) {
        setCurrentProject((prev) => {
          if (prev && prev.id === projectId) {
            return {
              ...prev,
              conversations: projectDetails.conversations.map(
                (convId: string) => convId
              ),
            };
          }
          return prev;
        });
      }
      setLoading(false);
    } catch (error) {
      console.error(
        `Error loading project conversations for ${projectId}:`,
        error
      );
      setLoading(false);
    }
  };

  const startNewProjectConversation = (projectId: string) => {
    setCurrentConversation(null);
    setAttachments([]);
    setActiveConversationId(null);
    sessionStorage.removeItem("conversation_id");
    setProjectChat(true);
  };

  const value: ConversationContextType = {
    currentConversation,
    conversations,
    loading,
    loadingMessage,
    selectedModel,
    attachments,
    activeConversationId,
    chatHistory,
    createProject,
    projectChat,
    currentProject,
    projects,
    selectedMode,
    setSelectedMode,
    setCreateProject,
    setProjectChat,
    setSelectedModel,
    sendMessage,
    loadConversation,
    startNewConversation,
    deleteConversation,
    addAttachment,
    removeAttachment,
    setLoadingMessage,
    setCurrentConversation,
    setConversations,
    setActiveConversationId,
    setChatHistory,
    fetchChatHistory,
    fetchAndSetConversation,
    createNewProject,
    addProjectFiles,
    removeProjectFile,
    setCurrentProject,
    fetchProjects,
    loadProjectConversations,
    startNewProjectConversation,
    streamingText,
    isStreaming,
    setStreamingText,
    setIsStreaming,
  };

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
};

// Custom hook
export const useConversation = () => {
  const context = useContext(ConversationContext);
  if (context === undefined) {
    throw new Error(
      "useConversation must be used within a ConversationProvider"
    );
  }
  return context;
};
